import React, { useMemo } from 'react'

import styled from '@emotion/styled'
import DOMPurify from 'dompurify'
import parse, { type DOMNode, domToReact, type HTMLReactParserOptions } from 'html-react-parser'

import { ArcadeLink, spacing, StructuredContent } from '@cash-design-system/react'

import { StyledStructuredContentForArticle } from '../styles'

const StyledStructuredContent = styled(StructuredContent)`
  ${StyledStructuredContentForArticle}

  /* Center images at the top level of the article, ie. not nested within paragraphs */
  & > div > img {
    margin: 0 auto;
    display: block;
    margin-bottom: ${spacing.s};
  }
`

const MultiImageContainer = styled.div`
  display: flex;
  justify-content: start;
  gap: ${spacing.s};
  flex-wrap: wrap;
`

// HTML parser options for handling multiple images and links
const baseParserOptions: HTMLReactParserOptions = {
  replace: (node, index) => {
    // Handle links with ArcadeLink
    if (node.type === 'tag' && node.name === 'a' && 'attribs' in node) {
      const href = node.attribs['href']
      const target = node.attribs['target']
      const rel = node.attribs['rel']

      return (
        <ArcadeLink href={href || '#'} target={target} rel={rel}>
          {domToReact(node.children as DOMNode[], baseParserOptions)}
        </ArcadeLink>
      )
    }

    // Handle multiple images (existing logic)
    if (
      node.type === 'tag' &&
      (node.name === 'p' || node.name === 'div') // target wrappers
    ) {
      const children = node.children || []

      // Check if all children are img tags and there are children
      const allChildrenAreImages =
        children.length > 0 && children.every(child => child.type === 'tag' && child.name === 'img')

      // If all children are images, wrap in centered div
      if (allChildrenAreImages) {
        return <MultiImageContainer>{domToReact(children as DOMNode[], baseParserOptions)}</MultiImageContainer>
      }
    }

    return undefined
  },
}

// Helper function to merge parser options
const mergeParserOptions = (
  baseOptions: HTMLReactParserOptions,
  customOptions?: HTMLReactParserOptions
): HTMLReactParserOptions => {
  if (!customOptions?.replace) {
    return baseOptions
  }

  return {
    replace: (node, index) => {
      // First try custom parser options
      const customResult = customOptions.replace?.(node, index)
      if (customResult !== undefined) {
        return customResult
      }

      // Then try base options
      return baseOptions.replace?.(node, index)
    },
  }
}

interface ParsedArticleContentProps {
  content: string
  customParserOptions?: HTMLReactParserOptions
  sanitize?: boolean
}

const ParsedArticleContent: React.FC<ParsedArticleContentProps> = ({
  content,
  customParserOptions,
  sanitize = true,
}) => {
  const parsedContent = useMemo(() => {
    const htmlContent = sanitize ? DOMPurify.sanitize(content) : content
    const options = mergeParserOptions(baseParserOptions, customParserOptions)
    return parse(htmlContent, options)
  }, [content, customParserOptions, sanitize])

  return <StyledStructuredContent>{parsedContent}</StyledStructuredContent>
}

export default ParsedArticleContent
